import { AttributesSectionEditorViewModel } from "@Alternatives/Attributes/Components/AttributesSectionEditor";
import AttributesSectionEditor from '@Alternatives/Attributes/Components/AttributesSectionEditor.vue';
import AttributesQueries from '@Alternatives/Attributes/Domain/queries-vue-component';
import { AttributeEditorReadModel, AttributeReadModel, CalcScriptResultReadModel } from '@Alternatives/Attributes/Domain/readmodels';
import { dateCompare1, generateName, strCompareLowerCase } from '@common/Common';
import CorporateNavigation from '@common/Components/CorporateNavigation.vue';
import { ICommandStatus } from "@cqrs/cqrs";
import { AttributeEditorDialogViewModel, SaveDateAttributeEventPayload, SaveEmailAttributeEventPayload, SaveMultilineAttributeEventPayload, SaveNumericAttributeEventPayload, SaveTextAttributeEventPayload, TestScriptEventPayload } from "@Quest/CorporateAttributes/Components/AttributeEditorDialog";
import AttributeEditorDialog from '@Quest/CorporateAttributes/Components/AttributeEditorDialog.vue';
import { CreateCorporateDateAttributeCommandV1, CreateCorporateEmailAttributeCommandV1, CreateCorporateMultilineAttributeCommandV1, CreateCorporateNumericAttributeCommandV1, CreateCorporateTextAttributeCommandV1, DeleteCorporateAttributeCommandV1, RenameCorporateAttributesSectionCommandV1, UpdateCorporateDateAttributeCommandV1, UpdateCorporateEmailAttributeCommandV1, UpdateCorporateMultilineAttributeCommandV1, UpdateCorporateNumericAttributeCommandV1, UpdateCorporateTextAttributeCommandV1 } from "@Quest/CorporateAttributes/Domain/commands-core";
import CorporateAttributesCommands from "@Quest/CorporateAttributes/Domain/commands-vue-component";
import CorporateAttributesQueries from "@Quest/CorporateAttributes/Domain/queries-vue-component";
import { ManageAllPageReadModel } from "@Quest/CorporateAttributes/Domain/readmodels";
import { ICommandBase } from '@Quest/cqrs';
import moment from "moment";
import Component, { mixins } from 'vue-class-component';

@Component({
    components: {
        CorporateNavigation, AttributesSectionEditor, AttributeEditorDialog
    }
})
export default class ManageAllPage extends mixins(AttributesQueries, CorporateAttributesQueries, CorporateAttributesCommands)
{
    get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
    get strategyId() { return this.$route.params.strategyId; }

    rm: ManageAllPageReadModel | null = null;

    get alternativesListId(): number { return this.rm!.AlternativesListId; }
    get tchProjectId(): number { return this.rm!.TchProjectId; }
    get attributes() { return this.rm?.Attributes ?? []; }

    searchPhrase: string | null = null;
    collapseAll: boolean = true;
    sections: SectionVM[] = [];

    attributeEditorDialog: AttributeEditorDialogViewModel = new AttributeEditorDialogViewModel();
    attributesSectionEditor = new AttributesSectionEditorViewModel();

    get sectionsView(): SectionVM[] 
    {
        return this.sections.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
    }

    getModifiedDateStr(rm: AttributeReadModel): string 
    {
        return moment(rm.Modified).format('MMM D, YYYY');
    }

    getModifiedTimeStr(rm: AttributeReadModel): string
    {
        return moment(rm.Modified).format('LT');
    }

    buildSections() 
    {
        // Create a map to hold sections by their names
        const sectionsMap = new Map();

        // Iterate over attributes and populate the map
        // This automatically ensures that each section name is unique
        this.attributes.forEach(attribute =>
        {
            if (!sectionsMap.has(attribute.Section))
            {
                sectionsMap.set(attribute.Section, new SectionVM(this, attribute.Section));
            }
        });

        // Replace the Sections array with new values from the map
        // This filters out empty sections and adds new ones
        this.sections = Array.from(sectionsMap.values());
    }

    toggleExpandAll()
    {
        if (this.collapseAll === true)
        {
            this.sections.forEach(s => s.Expanded = false);
        }
        else
        {
            this.sections.forEach(s => s.Expanded = true);
        }

        this.collapseAll = !this.collapseAll;
    }



    showAttributeCreator(section: string | null)
    {
        this.attributeEditorDialog.showCreator({
            defaultName: generateName("Attribute"),
            defaultSection: section,

            allowChangeAttributeType: true,
            allowInputTypeEvaluate: true,
            allowMultipleValuesEnabled: true,
        });

        this.runAttributeEditorQueryV1(this.workspaceId, this.tchProjectId, null);
    }

    showAttributeCloner(rm: AttributeReadModel)
    {
        this.attributeEditorDialog.showCloner(rm.Id, rm.Name);
        this.runAttributeEditorQueryV1(this.workspaceId, this.tchProjectId, rm.Id);
    }

    showAttributeEditor(rm: AttributeReadModel)
    {
        this.attributeEditorDialog.showEditor(rm.Id, rm.Name);
        this.runAttributeEditorQueryV1(this.workspaceId, this.tchProjectId, rm.Id);
    }


    showAttributesSectionEditor(s: SectionVM)
    {
        this.attributesSectionEditor.show(s.Name)
    }



    handleTestAttributeScript(e: TestScriptEventPayload)
    {
        this.runCalcAttributeScriptTestQueryV1(this.workspaceId, this.tchProjectId, this.alternativesListId, e.Script, e.ScriptTestInputs);
    }

    handleSaveNumericAttribute(e: SaveNumericAttributeEventPayload)
    {
        if (e.Id === 0)
        {
            this.invokeCreateCorporateNumericAttributeCommandV1(this.workspaceId, this.strategyId, 0,
                e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinValue, e.MaxValue, e.Script, e.DefinedValues);
        }
        else
        {
            this.invokeUpdateCorporateNumericAttributeCommandV1(this.workspaceId, this.strategyId, e.Id,
                e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinValue, e.MaxValue, e.Script, e.DefinedValues);
        }
    }

    handleSaveDateAttribute(e: SaveDateAttributeEventPayload)
    {
        if (e.Id === 0)
        {
            this.invokeCreateCorporateDateAttributeCommandV1(this.workspaceId, this.strategyId, 0,
                e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinValue, e.MaxValue, e.DefinedValues);
        }
        else
        {
            this.invokeUpdateCorporateDateAttributeCommandV1(this.workspaceId, this.strategyId, e.Id,
                e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinValue, e.MaxValue, e.DefinedValues);
        }
    }

    handleSaveEmailAttribute(e: SaveEmailAttributeEventPayload)
    {
        if (e.Id === 0)
        {
            this.invokeCreateCorporateEmailAttributeCommandV1(this.workspaceId, this.strategyId, 0,
                e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.DefinedValues);
        }
        else
        {
            this.invokeUpdateCorporateEmailAttributeCommandV1(this.workspaceId, this.strategyId, e.Id,
                e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.DefinedValues);
        }
    }

    handleSaveTextAttribute(e: SaveTextAttributeEventPayload)
    {
        if (e.Id === 0)
        {
            this.invokeCreateCorporateTextAttributeCommandV1(this.workspaceId, this.strategyId, 0,
                e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinLength, e.MaxLength, e.DefinedValues);
        }
        else
        {
            this.invokeUpdateCorporateTextAttributeCommandV1(this.workspaceId, this.strategyId, e.Id,
                e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinLength, e.MaxLength, e.DefinedValues);
        }
    }

    handleSaveMultilineAttribute(e: SaveMultilineAttributeEventPayload)
    {
        if (e.Id === 0)
        {
            this.invokeCreateCorporateMultilineAttributeCommandV1(this.workspaceId, this.strategyId, 0,
                e.Name, e.Description, e.Section, e.Rows, e.MinLength, e.MaxLength);
        }
        else
        {
            this.invokeUpdateCorporateMultilineAttributeCommandV1(this.workspaceId, this.strategyId, e.Id,
                e.Name, e.Description, e.Section, e.Rows, e.MinLength, e.MaxLength);
        }
    }



    handleDeleteCorporateAttributeCommand(attrId: number)
    {
        if (confirm("Are you sure?") == true)
        {
            this.invokeDeleteCorporateAttributeCommandV1(this.workspaceId, this.strategyId, attrId, true);
        }
    }

    handleRenameAttributesSectionCommand(newName: string | null, oldName: string | null)
    {
        this.invokeRenameCorporateAttributesSectionCommandV1(this.workspaceId, this.strategyId, oldName, newName);
    }

    mounted()
    {
        document.title = "Enterprise Attributes | TransparentChoice";
        this.runManageAllPageQueryV1(this.workspaceId, this.strategyId);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName === "ManageAllPageReadModel")
        {
            this.rm = model as ManageAllPageReadModel;
            this.buildSections();
        }
        else if (modelName === "AttributeReadModel")
        {
            const rm = model as AttributeReadModel;
            const curr = this.attributes.find(a => a.Id === rm.Id);

            if (curr != null)
            {
                this.attributes.remove(curr);
            }

            this.attributes.push(rm);
            this.buildSections();

            this.attributeEditorDialog.close();
        }
        else if (modelName === "AttributeEditorReadModel")
        {
            const rm = model as AttributeEditorReadModel;
            this.attributeEditorDialog.setData(rm);
        }
        else if (modelName === "CalcScriptResultReadModel")
        {
            this.attributeEditorDialog.setScriptTestResult(model as CalcScriptResultReadModel);
        }
    }

    onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
    {
        if (DeleteCorporateAttributeCommandV1.matchType(cmd))
        {
            this.rm.Attributes = this.rm.Attributes.filter(a => a.Id !== Number(correlationId));
            this.buildSections();
        }
        else if (RenameCorporateAttributesSectionCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as RenameCorporateAttributesSectionCommandV1;

            const section = this.sections.find(s => s.Name === cmd1.OldName);

            if (section)
            {
                section.attributes.forEach(a => a.Section = cmd1.NewName);
                this.buildSections();
            }

            this.attributesSectionEditor.close();
        }

        else if (CreateCorporateNumericAttributeCommandV1.matchType(cmd) ||
            CreateCorporateDateAttributeCommandV1.matchType(cmd) ||
            CreateCorporateEmailAttributeCommandV1.matchType(cmd) ||
            CreateCorporateTextAttributeCommandV1.matchType(cmd) ||
            CreateCorporateMultilineAttributeCommandV1.matchType(cmd))        
        {
            const attrId = Number(result);
            this.runAttributeByIdQueryV1(this.workspaceId, this.tchProjectId, this.alternativesListId, attrId);
        }
        else if (UpdateCorporateNumericAttributeCommandV1.matchType(cmd) ||
            UpdateCorporateDateAttributeCommandV1.matchType(cmd) ||
            UpdateCorporateEmailAttributeCommandV1.matchType(cmd) ||
            UpdateCorporateTextAttributeCommandV1.matchType(cmd) || 
            UpdateCorporateMultilineAttributeCommandV1.matchType(cmd))
        {
            const attrId = Number(correlationId);
            this.runAttributeByIdQueryV1(this.workspaceId, this.tchProjectId, this.alternativesListId, attrId);
        }
    }

    onCommandFailed(status: ICommandStatus)
    {
        if (CreateCorporateNumericAttributeCommandV1.matchType(status.Command) ||
            CreateCorporateDateAttributeCommandV1.matchType(status.Command) ||
            CreateCorporateEmailAttributeCommandV1.matchType(status.Command) ||
            CreateCorporateTextAttributeCommandV1.matchType(status.Command) ||
            CreateCorporateMultilineAttributeCommandV1.matchType(status.Command))        
        {
            this.attributeEditorDialog.displayValidationErrors(status.ValidationErrors);
        }
        else if (UpdateCorporateNumericAttributeCommandV1.matchType(status.Command) ||
            UpdateCorporateDateAttributeCommandV1.matchType(status.Command) ||
            UpdateCorporateEmailAttributeCommandV1.matchType(status.Command) ||
            UpdateCorporateTextAttributeCommandV1.matchType(status.Command) ||
            UpdateCorporateMultilineAttributeCommandV1.matchType(status.Command))
        {
            this.attributeEditorDialog.displayValidationErrors(status.ValidationErrors);
        }
        else if (DeleteCorporateAttributeCommandV1.matchType(status.Command))
        {
            this.attributeEditorDialog.displayValidationErrors(status.ValidationErrors);
        }
    }
}

class SectionVM
{
    page: ManageAllPage = null;

    Name: string;
    Expanded: boolean = true;
    OrderBy: string = "name";
    OrderByDescending: boolean = false;

    get attributes() { return this.page.attributes?.filter(a => a.Section?.toLowerCase() === this.Name?.toLowerCase()) ?? []; }

    get AttributesView()
    {
        let view = new Array(...this.attributes);

        if (this.page.searchPhrase)
        {
            view = view.filter(o => o.Name.toLowerCase().indexOf(this.page.searchPhrase.toLowerCase()) >= 0);
        }

        if (this.OrderBy === "name")
        {
            view = view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
        }
        else if (this.OrderBy === "modified")
        {
            view = view.sort((a, b) => dateCompare1(a.Modified, b.Modified));
        }

        if (this.OrderByDescending === true)
        {
            view = view.reverse();
        }

        return view;
    }

    constructor(page: ManageAllPage, name: string)
    {
        this.page = page;
        this.Name = name;
    }

    orderByName()
    {
        if (this.OrderBy === 'name')
        {
            this.OrderByDescending = !this.OrderByDescending;
        }
        else
        {
            this.OrderByDescending = false;
            this.OrderBy = 'name';
        }
    }

    orderByModified()
    {
        if (this.OrderBy === 'modified')
        {
            this.OrderByDescending = !this.OrderByDescending;
        }
        else
        {
            this.OrderByDescending = true;
            this.OrderBy = 'modified';
        }
    }
}